import DeletedWarning from "@/components/DeletedWarning";
import { EntityLink } from "@/components/data-table/components/EntityLink";
import PageContent from "@/components/PageContent";
import PageHeader from "@/components/PageHeader";
import RecordView from "@/components/record/RecordView";
import Spinner from "@/components/Spinner";
import { PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT } from "@/constants";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { InvoiceSpecificationCostsSection } from "@/modules/invoiceSpecifications/components/InvoiceSpecificationCostsSection";
import { InvoiceSpecificationLaborCostsInStoresSection } from "@/modules/invoiceSpecifications/components/InvoiceSpecificationLaborCostsInStoresSection";
import { SummarySection } from "@/modules/projects/components/invoiceSpecifications/section/SummarySection";
import { useInvoiceSpecificationDetailsBreadcrumbs } from "@/modules/invoiceSpecifications/hooks/useInvoiceSpecificationDetailsBreadcrumbs";
import { useTranslation } from "react-i18next";
import { useFormatters } from "@/lib/hooks/useFormatters";
import { useInvoiceSpecificationId } from "../hooks/useInvoiceSpecificationiId";
import { ResourceNotFound } from "@/components/ResourceNotFound";
import { useInvoiceSpecificationQuery } from "../hooks/useInvoiceSpecification";
import { generatePath } from "react-router-dom";

export const InvoiceSpecificationDetails = () => {
  const { t } = useTranslation();

  const { formatPeriod, formatDateTime, formatDate } = useFormatters();

  const invoiceSpecificationId = useInvoiceSpecificationId();

  const query = useInvoiceSpecificationQuery(invoiceSpecificationId);

  const breadcrumbItems = useInvoiceSpecificationDetailsBreadcrumbs({
    label: String(query.data?.ref_number),
  });

  if (query.isLoading) {
    return (
      <Spinner
        label={t("projectInvoiceSpecifications.loading")}
        className="w-full h-full"
      />
    );
  }

  if (!query.data) {
    return <ResourceNotFound />;
  }

  const invoiceSpecification = query.data;

  const editPath = generatePath(PATH_PROJECT_INVOICE_SPECIFICATIONS_EDIT, {
    projectId: invoiceSpecification.project_id,
    settlementId: invoiceSpecification.period_id,
    invoiceSpecificationId: invoiceSpecification.id,
  });

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <PageContent title={t("projectInvoiceSpecifications.title.multiple")}>
        <div className="grid grid-cols-1 gap-4">
          <ProjectDetailsAndNavigation
            projectId={invoiceSpecification.project_id}
          />
          <DeletedWarning
            show={!!invoiceSpecification.deleted_at}
            description={t("projectInvoiceSpecifications.deleted.description")}
          />
          <RecordView
            isOpen
            query={query}
            translationKey="projectInvoiceSpecifications"
            header={() =>
              t("projectInvoiceSpecifications.sections.basicData.header")
            }
          >
            <RecordView.Field
              name="ref_number"
              renderer={(value) => (
                <EntityLink internalTo={editPath}>{value}</EntityLink>
              )}
            />
            <RecordView.Field
              name="period"
              renderer={(value) => formatPeriod(value)}
            />
            <RecordView.Field name="invoicing_company" />
            <RecordView.Field name="services_and_goods" />
            <RecordView.Field name="primary_manager" />
            <RecordView.Field name="client" />
            <RecordView.Field name="code" />
            <RecordView.Field
              name="issued_at"
              renderer={(value) => formatDate(value)}
            />
            <RecordView.Field
              name="created_at"
              renderer={(value) => formatDateTime(value)}
            />
            <RecordView.Field name="comment" />
          </RecordView>
          <SummarySection invoiceSpecificationId={invoiceSpecificationId} />
          <InvoiceSpecificationCostsSection
            invoiceSpecificationId={invoiceSpecificationId}
          />
          <InvoiceSpecificationLaborCostsInStoresSection
            invoiceSpecificationId={invoiceSpecificationId}
            projectDependsOn={invoiceSpecification.project_depends_on}
          />
        </div>
      </PageContent>
    </>
  );
};
